/**
 * sliceFilters — pure dimension extraction + slice-matching for the Slicer.
 *
 * Pure functions only. No React, no DOM, no I/O. The Slicer UI calls these
 * to derive bucket assignments for every trade and to filter trade sets
 * down to whatever combination the user has selected.
 *
 * Slice shape (what the UI passes around):
 *   {
 *     time:      ['opening-drive', 'mid-morning'],   // array of bucket IDs
 *     vix:       ['VIX 21-25'],                      // array of confluence chip names
 *     direction: ['long'],                            // array of 'long' | 'short'
 *     dayOfWeek: ['tue', 'wed'],                     // array of 'mon'..'fri'
 *     range:     'last-30d',                          // single preset ID
 *   }
 *
 * Conventions:
 *   - Empty array (or undefined) for a dimension = NO filter on that dim
 *   - Multi-value within a dim = OR (matches any of)
 *   - Across dims = AND (matches all dims)
 *   - Unknown/missing trade fields = excluded from any filter that uses that field
 *     (so filtering by `time` excludes trades with no entryTime, not silently includes)
 */

// ── Time-of-day buckets (ET) ─────────────────────────────────────
//
// Encoded as "minutes from midnight". Asia wraps midnight (18:00→03:00 next
// day) — represented with `wrapsMidnight: true` so the matcher uses an OR
// instead of an AND.
//
// 5pm-6pm ET is intentionally NOT a bucket — market closed, no trades land
// there. If a trade did somehow have an entry at 17:30 (manual entry typo),
// it'd be excluded from every time bucket and surface as "needs review" via
// the "no time bucket matched" branch in extractDimensions.

export const TIME_BUCKETS = [
  { id: 'asia',          label: 'Asia Session',   start: '18:00', end: '03:00', wrapsMidnight: true },
  { id: 'london',        label: 'London Session', start: '03:00', end: '08:00' },
  { id: 'us-premarket',  label: 'US Pre-Market',  start: '08:00', end: '09:30' },
  { id: 'opening-drive', label: 'Opening Drive',  start: '09:30', end: '10:30' },
  { id: 'mid-morning',   label: 'Mid-Morning',    start: '10:30', end: '12:00' },
  { id: 'lunch',         label: 'Lunch',          start: '12:00', end: '13:30' },
  { id: 'afternoon',     label: 'Afternoon',      start: '13:30', end: '15:00' },
  { id: 'close',         label: 'Close',          start: '15:00', end: '16:00' },
  { id: 'after-hours',   label: 'After-Hours',    start: '16:00', end: '17:00' },
];

// ── Day-of-week buckets ──────────────────────────────────────────

export const DAY_BUCKETS = [
  { id: 'mon', label: 'Monday',    dow: 1 },
  { id: 'tue', label: 'Tuesday',   dow: 2 },
  { id: 'wed', label: 'Wednesday', dow: 3 },
  { id: 'thu', label: 'Thursday',  dow: 4 },
  { id: 'fri', label: 'Friday',    dow: 5 },
];

// ── Direction buckets ────────────────────────────────────────────

export const DIRECTION_BUCKETS = [
  { id: 'long',  label: 'Long'  },
  { id: 'short', label: 'Short' },
];

// ── Date-range presets ───────────────────────────────────────────
//
// Each preset returns a day-count back from "now". 'ytd' is special-cased
// (back to Jan 1 of the current year). 'all' = no filter.

export const RANGE_PRESETS = [
  { id: 'last-7d',   label: 'Last 7 days',   days: 7 },
  { id: 'last-30d',  label: 'Last 30 days',  days: 30 },
  { id: 'last-90d',  label: 'Last 90 days',  days: 90 },
  { id: 'last-180d', label: 'Last 180 days', days: 180 },
  { id: 'ytd',       label: 'Year to date',  ytd: true },
  { id: 'all',       label: 'All time' },
];

// ── Time parsing ─────────────────────────────────────────────────

/**
 * Parse "HH:MM" or "HH:MM AM/PM" or "HH:MM:SS" into minutes-from-midnight.
 * Returns null when unparseable. Mirrors the lenient parser used elsewhere
 * in the codebase (eventAutoTag, etc.).
 */
const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const s = timeStr.trim();
  // "HH:MM AM/PM" or "HH:MM:SS AM/PM"
  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const meridiem = ampm[3].toUpperCase();
    if (meridiem === 'PM' && h !== 12) h += 12;
    if (meridiem === 'AM' && h === 12) h = 0;
    return h * 60 + m;
  }
  // "HH:MM" or "HH:MM:SS"
  const hm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) {
    const h = parseInt(hm[1], 10);
    const m = parseInt(hm[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) return h * 60 + m;
  }
  return null;
};

/**
 * Parse "HH:MM" → minutes only (no AM/PM). Used for bucket boundaries which
 * are always 24-hour format in the bucket constants.
 */
const parseHM = (s) => {
  const m = String(s).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
};

// ── Time bucket assignment ───────────────────────────────────────

const matchesTimeBucket = (timeMin, bucket) => {
  if (timeMin == null) return false;
  const startMin = parseHM(bucket.start);
  const endMin = parseHM(bucket.end);
  if (startMin == null || endMin == null) return false;
  if (bucket.wrapsMidnight) {
    // e.g. asia: 18:00..03:00 → match if time >= 18:00 OR time < 03:00
    return timeMin >= startMin || timeMin < endMin;
  }
  // Standard half-open interval [start, end)
  return timeMin >= startMin && timeMin < endMin;
};

/**
 * Resolve the bucket ID a trade's entryTime falls into. Returns null when
 * the trade has no parseable entryTime or doesn't fit any bucket (e.g.,
 * during the 5-6pm ET market-closed hour — should never happen on real
 * fills, but possible on manual-entry errors).
 */
export const resolveTimeBucket = (entryTime) => {
  const min = parseTimeToMinutes(entryTime);
  if (min == null) return null;
  for (const b of TIME_BUCKETS) {
    if (matchesTimeBucket(min, b)) return b.id;
  }
  return null;
};

// ── Day-of-week assignment ───────────────────────────────────────

/**
 * Returns day-of-week bucket ID for a trade date string (YYYY-MM-DD).
 * Uses local-time fields (not UTC parse) so the day doesn't shift in
 * negative-UTC timezones. Returns null for weekends (Sat/Sun) or invalid.
 */
export const resolveDayOfWeek = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);  // 1-12
  const day = parseInt(m[3], 10);
  // Validate ranges BEFORE constructing — JS Date silently overflows
  // (new Date(2026, 12, 1) rolls to Jan 2027), so we'd silently return
  // 'thu' for an invalid '2026-13-01' without this check.
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  const dt = new Date(year, month - 1, day);
  if (isNaN(dt.getTime())) return null;
  // Defense-in-depth: round-trip check catches things like Feb 30
  if (dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  const dow = dt.getDay();
  const bucket = DAY_BUCKETS.find(b => b.dow === dow);
  return bucket ? bucket.id : null;
};

// ── VIX dimension (from confluence chips) ────────────────────────

/**
 * Find the VIX bucket chip name attached to a trade, if any. VIX chips
 * are user-defined confluences with category 'VIX' and names like
 * 'VIX 21-25'. A trade can technically have multiple VIX chips (data-
 * entry error) — we return the first one we see, deterministic order.
 *
 * The slicer matches by exact chip name, so multiple-VIX-on-one-trade
 * just means the trade matches whichever names appear.
 *
 * @param {Object} trade
 * @param {Object} confluenceLookup  Map of confluenceId → { name, category }
 * @returns {string[]}  Array of VIX chip names (usually 0 or 1 entries)
 */
export const resolveVixChips = (trade, confluenceLookup) => {
  if (!trade?.confluences || !Array.isArray(trade.confluences)) return [];
  if (!confluenceLookup) return [];
  const out = [];
  for (const id of trade.confluences) {
    const conf = confluenceLookup[id];
    if (!conf) continue;
    const cat = (conf.category || '').toUpperCase();
    const name = conf.name || '';
    if (cat === 'VIX' || name.toUpperCase().startsWith('VIX ')) {
      out.push(name);
    }
  }
  return out;
};

// ── Date-range resolution ────────────────────────────────────────

/**
 * Given a range preset ID + "now" timestamp, return the inclusive
 * (fromDateStr, toDateStr) range as YYYY-MM-DD or null for 'all'.
 *
 * Uses LOCAL date arithmetic so day boundaries align with the user's
 * trading-day labeling — trades stored as 'YYYY-MM-DD' in ET match
 * cleanly against the resolved range.
 */
export const resolveRangePreset = (presetId, nowMs = Date.now()) => {
  const preset = RANGE_PRESETS.find(p => p.id === presetId);
  if (!preset || preset.id === 'all') return null;
  const now = new Date(nowMs);
  const fmtDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const to = fmtDate(now);
  if (preset.ytd) {
    const from = `${now.getFullYear()}-01-01`;
    return { from, to };
  }
  if (typeof preset.days === 'number') {
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - preset.days);
    return { from: fmtDate(fromDate), to };
  }
  return null;
};

// ── extractDimensions: derive all bucket assignments for a trade ──

/**
 * Flatten all dimension values for a single trade. Used by both the
 * filter matcher AND the per-value count display (so the dropdown can
 * show "Opening Drive · 47" without re-walking the trade list per dim).
 *
 * @param {Object} trade
 * @param {Object} [opts.confluenceLookup]  Map of confluenceId → confluence
 * @returns {Object} { time, vix, direction, dayOfWeek }
 *   - time:      string bucket ID or null
 *   - vix:       array of VIX chip names (may be empty)
 *   - direction: 'long' | 'short' | null
 *   - dayOfWeek: string day ID or null
 */
export const extractDimensions = (trade, opts = {}) => {
  if (!trade || typeof trade !== 'object') {
    return { time: null, vix: [], direction: null, dayOfWeek: null };
  }
  return {
    time:      resolveTimeBucket(trade.entryTime),
    vix:       resolveVixChips(trade, opts.confluenceLookup),
    direction: trade.direction === 'long' || trade.direction === 'short' ? trade.direction : null,
    dayOfWeek: resolveDayOfWeek(trade.date),
  };
};

// ── tradeMatchesSlice + filterTradesBySlice ──────────────────────

/**
 * True iff the trade matches every active dimension in the slice.
 * Empty/undefined dimension arrays are treated as "no filter on this dim".
 */
export const tradeMatchesSlice = (trade, slice, opts = {}) => {
  if (!trade) return false;
  if (!slice || typeof slice !== 'object') return true;

  const dims = extractDimensions(trade, opts);

  // time
  if (Array.isArray(slice.time) && slice.time.length > 0) {
    if (!dims.time || !slice.time.includes(dims.time)) return false;
  }
  // direction
  if (Array.isArray(slice.direction) && slice.direction.length > 0) {
    if (!dims.direction || !slice.direction.includes(dims.direction)) return false;
  }
  // dayOfWeek
  if (Array.isArray(slice.dayOfWeek) && slice.dayOfWeek.length > 0) {
    if (!dims.dayOfWeek || !slice.dayOfWeek.includes(dims.dayOfWeek)) return false;
  }
  // vix (chip-name based — OR across the trade's chips)
  if (Array.isArray(slice.vix) && slice.vix.length > 0) {
    if (dims.vix.length === 0) return false;
    const hasAny = slice.vix.some(v => dims.vix.includes(v));
    if (!hasAny) return false;
  }
  // range — date-string compare against trade.date (YYYY-MM-DD lex-sortable)
  if (slice.range && slice.range !== 'all') {
    const window = resolveRangePreset(slice.range, opts.nowMs);
    if (window) {
      if (!trade.date || trade.date < window.from || trade.date > window.to) return false;
    }
  }

  return true;
};

/**
 * Filter a trade array down to the slice. Returns a new array; unchanged
 * trades keep their original reference (so consumers can shallow-eq if
 * needed). Pure function — no mutation.
 */
export const filterTradesBySlice = (trades, slice, opts = {}) => {
  if (!Array.isArray(trades)) return [];
  if (!slice || !hasAnyActiveDimension(slice)) return trades.slice(0);
  return trades.filter(t => tradeMatchesSlice(t, slice, opts));
};

/**
 * True when the slice has at least one active dimension. Cheap pre-check
 * to skip the filter pass when no dimensions are active.
 */
export const hasAnyActiveDimension = (slice) => {
  if (!slice) return false;
  if (Array.isArray(slice.time)      && slice.time.length      > 0) return true;
  if (Array.isArray(slice.vix)       && slice.vix.length       > 0) return true;
  if (Array.isArray(slice.direction) && slice.direction.length > 0) return true;
  if (Array.isArray(slice.dayOfWeek) && slice.dayOfWeek.length > 0) return true;
  if (slice.range && slice.range !== 'all') return true;
  return false;
};

// ── enumerateValuesInScope: live per-value trade counts for dropdowns ──

/**
 * For a given dimension, count how many trades in the "scope" (typically
 * the trades already filtered by OTHER active dimensions) would match each
 * possible value of this dimension. Drives the per-value badges in the
 * dropdown ("Opening Drive · 47").
 *
 * @param {Array} scopeTrades  Trades to count over
 * @param {string} dim         One of 'time' | 'vix' | 'direction' | 'dayOfWeek'
 * @param {Object} [opts.confluenceLookup]
 * @returns {Object} Map of bucket-id → count
 */
export const enumerateValuesInScope = (scopeTrades, dim, opts = {}) => {
  const counts = {};
  if (!Array.isArray(scopeTrades) || !dim) return counts;
  // Seed every known bucket at 0 so the dropdown can render greyed-out values
  // without missing entries when no trade matches them in the current scope.
  switch (dim) {
    case 'time':
      for (const b of TIME_BUCKETS) counts[b.id] = 0;
      break;
    case 'dayOfWeek':
      for (const b of DAY_BUCKETS) counts[b.id] = 0;
      break;
    case 'direction':
      for (const b of DIRECTION_BUCKETS) counts[b.id] = 0;
      break;
    case 'vix':
      // VIX values are user-defined; we discover them as we walk.
      break;
    default:
      return counts;
  }
  for (const trade of scopeTrades) {
    const dims = extractDimensions(trade, opts);
    if (dim === 'vix') {
      for (const v of dims.vix) {
        counts[v] = (counts[v] || 0) + 1;
      }
    } else {
      const val = dims[dim];
      if (val) counts[val] = (counts[val] || 0) + 1;
    }
  }
  return counts;
};

// ── Slice summary (for clipboard copy) ───────────────────────────

/**
 * Render a one-line human-readable summary of the slice. Used by the
 * "copy to clipboard" feature for Discord shareability.
 *
 * Example: "Opening Drive + Mid-Morning · VIX 21-25 · Long · Tue · Last 30d"
 */
export const summarizeSlice = (slice) => {
  if (!slice || !hasAnyActiveDimension(slice)) return 'All trades';
  const parts = [];

  if (Array.isArray(slice.time) && slice.time.length > 0) {
    parts.push(slice.time.map(id => TIME_BUCKETS.find(b => b.id === id)?.label || id).join(' + '));
  }
  if (Array.isArray(slice.vix) && slice.vix.length > 0) {
    parts.push(slice.vix.join(' / '));
  }
  if (Array.isArray(slice.direction) && slice.direction.length > 0) {
    parts.push(slice.direction.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(' / '));
  }
  if (Array.isArray(slice.dayOfWeek) && slice.dayOfWeek.length > 0) {
    parts.push(slice.dayOfWeek.map(id => {
      const b = DAY_BUCKETS.find(x => x.id === id);
      return b ? b.label.slice(0, 3) : id;
    }).join('/'));
  }
  if (slice.range && slice.range !== 'all') {
    const p = RANGE_PRESETS.find(x => x.id === slice.range);
    if (p) parts.push(p.label);
  }
  return parts.join(' · ');
};

// ── Empty slice ──────────────────────────────────────────────────

export const EMPTY_SLICE = Object.freeze({
  time: [],
  vix: [],
  direction: [],
  dayOfWeek: [],
  range: 'all',
});

// ── Test-only exports ────────────────────────────────────────────

export { parseTimeToMinutes, parseHM, matchesTimeBucket };
